/**
 * LiquidCard — Layer-1 no-line editorial card.
 *
 * No-line rule: boundaries via tonal shifts, not borders.
 * Tone scales opacity/shadow to create depth hierarchy.
 * When elevated=true, adds border + background for solid card use.
 *
 * @layer-1 — propagates to all features automatically
 */
import { cn } from "@/shared/lib/utils";

type Tone = "lowest" | "low" | "high" | "highest";

interface LiquidCardProps {
  tone?: Tone;
  elevated?: boolean;
  /** When true, adds background + border for solid card appearance (used for panels/cards) */
  solid?: boolean;
  className?: string;
  children: React.ReactNode;
}

const toneMap: Record<Tone, string> = {
  lowest: "bg-[var(--color-surface-lowest)]",
  low: "bg-[var(--color-surface-low)]",
  high: "bg-[var(--color-surface-high)]",
  highest: "bg-[var(--color-surface-highest)]",
};

export function LiquidCard({
  tone = "low",
  elevated,
  solid,
  className,
  children,
}: LiquidCardProps) {
  return (
    <div
      className={cn(
        "rounded-[var(--radius-3xl)]",
        toneMap[tone],
        solid && "border border-[var(--border-subtle)]",
        elevated && "shadow-[var(--shadow-ambient)]",
        className
      )}
    >
      {children}
    </div>
  );
}
